// =============================================================================
// ADMIN ROLES SLICE
// Manages the roles[] list that powers the Role Management submodule under Admin.
//
// State shape:
//   roles[]    → the master list fetched from the API; source of truth for the table
//   isLoading  → true while the initial fetchRoles call is in-flight (shows skeleton)
//   isSaving   → true while create / update / toggle is in-flight (disables Save btn)
//   error      → last error message from any thunk; null when healthy
//
// All async work lives in adminThunks.ts (createAsyncThunk).
// This file only declares state shape + how to handle each thunk outcome.
// =============================================================================

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Role } from '../../screens/Admin/pages/RoleManagement/types';
import {
  fetchRoles,
  createRole,
  updateRole,
  toggleRoleStatus,
  deleteRole,
  fetchLocationTypes,
} from '../thunks/adminThunks';

// =============================================================================
// STATE INTERFACE
// =============================================================================

interface AdminRolesState {
  /**
   * The current page of roles returned by GET /admin/role-management/roles.
   * The API is paginated; this is only the current page, not the full list.
   */
  roles: Role[];

  /**
   * True while fetchRoles is in-flight.
   * Used to show a loading skeleton in RoleTable on first load.
   */
  isLoading: boolean;

  /**
   * True while createRole, updateRole, or toggleRoleStatus is in-flight.
   * Kept separate from isLoading so the table stays visible (not skeletonised)
   * while a save/toggle operation runs in the background.
   */
  isSaving: boolean;

  /**
   * Stores the most recent error message from any thunk.
   * null = no error. Set back to null on the next pending action.
   * Surfaced in the UI via the infoModal in RoleManagementPage.
   */
  error: string | null;

  /**
   * Pagination metadata from the last API response.
   * Used to render page count, determine if more pages exist, etc.
   */
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };

  /**
   * Current sort state so UI can reflect which column is sorted and in what direction.
   */
  sortBy: 'roleName' | 'modifiedAt';
  sortDirection: 'asc' | 'desc';

  /** Filter options returned by the API response metadata. */
  filters: {
    availableIn: string[];
    locationTypes: string[];
    statuses: string[];
    organizationTypes: string[];
    threePlStatuses: string[];
    roleTypes: string[];
  };
  /**
   * Location type options fetched from GET /location-types.
   * Populated when the Add Role form is opened.
   * Used by RoleFormModal to populate the Location Type checkboxes.
   */
  locationTypes: string[];

  /** True while fetchLocationTypes is in-flight. */
  isLoadingLocationTypes: boolean;
}

const initialState: AdminRolesState = {
  roles: [],
  isLoading: false,
  isSaving: false,
  error: null,
  pagination: {
    pageNumber: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false,
  },
  sortBy: 'roleName' as const,
  sortDirection: 'asc' as const,
  filters: {
    availableIn: [],
    locationTypes: [],
    statuses: [],
    organizationTypes: [],
    threePlStatuses: [],
    roleTypes: [],
  },
  locationTypes: [],
  isLoadingLocationTypes: false,
};

/**
 * Normalizes a raw API role item into the frontend Role shape.
 * The GET /roles endpoint returns field names that differ from the frontend model:
 *   API: roleName, locationTypes, status (boolean)
 *   Frontend: name, locTypes, status ('Enabled'|'Disabled')
 */
function normalizeRole(raw: any): Role {
  return {
    id:                raw.id ?? raw.vpZone ?? raw.roleName ?? raw.name ?? '',
    name:              raw.name ?? raw.roleName ?? '',
    vpZone:            raw.vpZone,
    availableIn:       raw.availableIn ?? [],
    locTypes:          raw.locTypes ?? raw.locationTypes ?? [],
    status:
      typeof raw.status === 'boolean'
        ? (raw.status ? 'Enabled' : 'Disabled')
        : (raw.status ?? 'Enabled'),
    organizationTypes: raw.organizationTypes ?? [],
    threePlStatus:     raw.threePlStatus ?? undefined,
    roleType:          raw.roleType ?? undefined,
    vpZones:           raw.vpZones ?? [],
    kfaReferenceCount: raw.kfaReferenceCount,
    activeLaborPlanCount: raw.activeLaborPlanCount,
    createdAt:         raw.createdAt,
    updatedAt:         raw.updatedAt ?? raw.modifiedAt,
  };
}

// =============================================================================
// SLICE
// extraReducers handles all the async thunk lifecycle actions
// (pending → fulfilled → rejected) so the slice stays clean.
// =============================================================================

const adminRolesSlice = createSlice({
  name: 'adminRoles',
  initialState,

  reducers: {
    /**
     * Manually clear the error — called when the user dismisses the error modal
     * so stale errors don't re-appear on the next open.
     */
    clearAdminRolesError(state) {
      state.error = null;
    },
  },

  extraReducers: (builder) => {

    // ─── fetchRoles ──────────────────────────────────────────────────────────
    // GET /admin/roles  →  replaces the entire roles[] array
    builder
      .addCase(fetchRoles.pending, (state) => {
        // Show skeleton table while loading
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        // Handle both simple array response and paginated response
        if (Array.isArray(action.payload)) {
          state.roles = action.payload.map(normalizeRole);
        } else if (action.payload && Array.isArray(action.payload.content)) {
          state.roles = action.payload.content.map(normalizeRole);
          if (action.payload.pageNumber !== undefined) {
            state.pagination.pageNumber = action.payload.pageNumber;
            state.pagination.pageSize = action.payload.pageSize;
            state.pagination.totalElements = action.payload.totalElements;
            state.pagination.totalPages = action.payload.totalPages;
            state.pagination.first = action.payload.first;
            state.pagination.last = action.payload.last;
          }
          if (action.payload.sortBy) state.sortBy = action.payload.sortBy;
          if (action.payload.sortDirection) state.sortDirection = action.payload.sortDirection;
          if (action.payload.filters) {
            state.filters.availableIn = action.payload.filters.availableIn ?? state.filters.availableIn;
            state.filters.locationTypes = action.payload.filters.locationTypes ?? state.filters.locationTypes;
            state.filters.statuses = action.payload.filters.statuses ?? state.filters.statuses;
            state.filters.organizationTypes = action.payload.filters.organizationTypes ?? state.filters.organizationTypes;
            state.filters.threePlStatuses = action.payload.filters.threePlStatuses ?? state.filters.threePlStatuses;
            state.filters.roleTypes = action.payload.filters.roleTypes ?? state.filters.roleTypes;
          }
        }
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Failed to load roles';
      });

    // ─── createRole ──────────────────────────────────────────────────────────
    // POST /admin/roles  →  appends the new role the server returns
    builder
      .addCase(createRole.pending, (state) => {
        // Disable Save button; keep table visible
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state) => {
        // Handler dispatches fetchRoles after success to get the fresh server state.
        state.isSaving = false;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to create role';
      });

    // ─── updateRole ──────────────────────────────────────────────────────────
    // PUT /admin/roles/:id  →  replaces the matching role in-place
    builder
      .addCase(updateRole.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state) => {
        // Handler dispatches fetchRoles after success to pick up any name/field changes.
        state.isSaving = false;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to update role';
      });

    // ─── toggleRoleStatus ────────────────────────────────────────────────────
    // PATCH /admin/roles/:id/status  →  replaces the matching role in-place
    // Treated the same as updateRole from the Redux perspective — the server
    // returns the full updated role object so we swap it in-place.
    builder
      .addCase(toggleRoleStatus.pending, (state) => {
        // isSaving disables the toggle briefly to prevent double-clicks
        state.isSaving = true;
        state.error = null;
      })
      .addCase(toggleRoleStatus.fulfilled, (state, action: PayloadAction<Role>) => {
        state.isSaving = false;
        const idx = state.roles.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) {
          state.roles[idx] = action.payload;
        }
      })
      .addCase(toggleRoleStatus.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to update role status';
      });

    // ─── deleteRole ───────────────────────────────────────────────────────────
    // DELETE /admin/role-management/fixed-hour-roles/{vpZone}
    // Removes the deleted role from state immediately; handler shows a toast.
    builder
      .addCase(deleteRole.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action: PayloadAction<string>) => {
        state.isSaving = false;
        // Remove by id (vpZone) or name — both hold the same value per API spec
        state.roles = state.roles.filter(
          (r) => r.id !== action.payload && r.name !== action.payload
        );
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to delete role';
      });

    // ─── fetchLocationTypes ───────────────────────────────────────────────────
    // GET /admin/role-management/location-types
    // Populates the location type list used by the Add/Edit form modal.
    builder
      .addCase(fetchLocationTypes.pending, (state) => {
        state.isLoadingLocationTypes = true;
      })
      .addCase(fetchLocationTypes.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.isLoadingLocationTypes = false;
        if (action.payload?.length) {
          state.locationTypes = action.payload;
        }
      })
      .addCase(fetchLocationTypes.rejected, (state) => {
        // Silently fail — the form falls back to hardcoded defaults
        state.isLoadingLocationTypes = false;
      });
  },
});

// =============================================================================
// ACTIONS
// =============================================================================

export const { clearAdminRolesError } = adminRolesSlice.actions;
export default adminRolesSlice.reducer;

// =============================================================================
// SELECTORS
// Typed selector functions keep component code clean:
//   const roles = useSelector(selectRoles);
// instead of:
//   const roles = useSelector((state: RootState) => state.adminRoles.roles);
//
// The RootState import would create a circular dependency here (store/index.ts
// imports this slice), so we use a local inline type for the argument.
// =============================================================================

type SliceState = { adminRoles: AdminRolesState };

/** Current page of roles from the API. */
export const selectRoles            = (state: SliceState) => state.adminRoles.roles;

/** True while GET /admin/role-management/roles is in-flight — drive skeleton loader. */
export const selectRolesLoading     = (state: SliceState) => state.adminRoles.isLoading;

/** True while any write thunk (create / update / toggle) is in-flight. */
export const selectRolesSaving      = (state: SliceState) => state.adminRoles.isSaving;

/** Last error message, or null if no error. */
export const selectRolesError       = (state: SliceState) => state.adminRoles.error;

/** Pagination metadata from the last API response. */
export const selectRolesPagination  = (state: SliceState) => state.adminRoles.pagination;

/** Current sort state (column + direction). */
export const selectRolesSort        = (state: SliceState) => ({
  sortBy: state.adminRoles.sortBy,
  sortDirection: state.adminRoles.sortDirection,
});

/** Available filter options returned by API metadata. */
export const selectRolesFilterOptions = (state: SliceState) => state.adminRoles.filters;

/** Location type options from GET /location-types — used by the Add/Edit form. */
export const selectLocationTypes = (state: SliceState) => state.adminRoles.locationTypes;

/** True while fetchLocationTypes is in-flight. */
export const selectLocationTypesLoading = (state: SliceState) => state.adminRoles.isLoadingLocationTypes;
